import { APIService } from 'app/services/api.service';
import {
  AbstractControl,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators
} from '@angular/forms';
import { environment } from 'environments/environment';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Component, Inject, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { UntilDestroy } from '@ngneat/until-destroy';
import { DishesService } from 'app/services/dishes.service';
import { GecoPosCreateOrUpdateOrganizationRequest } from 'app/models/gecoPosCreateOrUpdateOrganizationRequest';
import { PosOrganizationModel } from "app/reducers/pos.reducer";

@UntilDestroy()
@Component({
  selector: 'app-geco-pos-organization-modal',
  templateUrl: './geco-pos-organization-modal.component.html',
  styleUrls: ['./geco-pos-organization-modal.component.scss']
})
export class GecoPosOrganizationModalComponent implements OnInit {
  private static readonly OrderCreatePath = '/api/external-systems/gecopossales/orders/create';
  private static readonly OrderStatusUpdatePath = '/api/external-systems/gecopossales/orders/status/update';
  private static readonly StopListUpdatePath = '/api/external-systems/gecopossales/stoplists/update';

  gecoPosOrganizationForm: UntypedFormGroup;
  updateOrganizationRequest: GecoPosCreateOrUpdateOrganizationRequest;

  constructor(
    public dialogRef: MatDialogRef<any>,
    @Inject(MAT_DIALOG_DATA) public item: GecoPosOrganizationWrapper | null,
    public api: APIService,
    private formBuilder: UntypedFormBuilder,
    public translate: TranslateService,
    private dishesService: DishesService
  ) {
  }

  /** Хост хаба текущего контура: с ним обязаны совпадать вебхуки организации. */
  get hubHost(): string {
    return GecoPosOrganizationModalComponent.hostOf(environment.baseUrl) ?? '';
  }

  ngOnInit(): void {
    const defaultApiKey = 'r6t23TUmo4jByVc65UXrjRZvvz2Qbupu';
    // Адреса строятся от baseUrl контура, а не литералом: вебхук в чужой хаб не находит там
    // пайпа, отвечает 400, и заказ до POS не доходит — молча, до разбора логов.
    const hubBaseUrl = environment.baseUrl.replace(/\/+$/, '');
    const defaultOrderCreateWebhookUrl = `${hubBaseUrl}${GecoPosOrganizationModalComponent.OrderCreatePath}`;
    const defaultOrderStatusUpdateWebhookUrl = `${hubBaseUrl}${GecoPosOrganizationModalComponent.OrderStatusUpdatePath}`;
    const stopListUpdateWebhookUrl = `${hubBaseUrl}${GecoPosOrganizationModalComponent.StopListUpdatePath}`;
    const hubHostValidator = GecoPosOrganizationModalComponent.hubHostValidator(this.hubHost);
    this.updateOrganizationRequest = {
      id: this.item.organization.id,
      name: this.item.organization.name,
      apiKey: defaultApiKey,
      orderCreateWebhookUrl: defaultOrderCreateWebhookUrl,
      orderStatusUpdateWebhookUrl: defaultOrderStatusUpdateWebhookUrl,
      stopListUpdateWebhookUrl: stopListUpdateWebhookUrl,
      importFromPos: false,
    };

    this.gecoPosOrganizationForm = this.formBuilder.group({
      name: new UntypedFormControl(this.item.organization ? this.item.organization.name : '', Validators.required),
      apiKey: new UntypedFormControl(this.item.organization
          ? this.item.organization.apiKey ? this.item.organization.apiKey : defaultApiKey
          : defaultApiKey,
        [Validators.required]),
      orderCreateWebhookUrl: new UntypedFormControl(this.item.organization
        ? this.item.organization.orderCreateWebhookUrl
          ? this.item.organization.orderCreateWebhookUrl
          : defaultOrderCreateWebhookUrl
        : defaultOrderCreateWebhookUrl, [hubHostValidator]),
      orderStatusUpdateWebhookUrl: new UntypedFormControl(this.item.organization
        ? this.item.organization.orderStatusUpdateWebhookUrl
          ? this.item.organization.orderStatusUpdateWebhookUrl
          : defaultOrderStatusUpdateWebhookUrl
        : defaultOrderStatusUpdateWebhookUrl, [hubHostValidator]),
      stopListUpdateWebhookUrl: new UntypedFormControl(this.item.organization
        ? this.item.organization.stopListUpdateWebhookUrl
          ? this.item.organization.stopListUpdateWebhookUrl
          : stopListUpdateWebhookUrl
        : stopListUpdateWebhookUrl, [hubHostValidator]),
      importFromPos: new UntypedFormControl(this.item.organization
        ? this.item.organization.importFromPos
          ? this.item.organization.importFromPos
          : false
        : false),
    });

    this.gecoPosOrganizationForm.valueChanges.subscribe(val => {
      const org = val as GecoPosCreateOrUpdateOrganizationRequest;
      this.updateOrganizationRequest = {
        id: '',
        name: org.name,
        apiKey: org.apiKey,
        orderCreateWebhookUrl: org.orderCreateWebhookUrl,
        orderStatusUpdateWebhookUrl: org.orderStatusUpdateWebhookUrl,
        stopListUpdateWebhookUrl: org.stopListUpdateWebhookUrl,
        importFromPos: org.importFromPos,
      };
    });
  }

  add(): void {
    const createOrganizationRequest: GecoPosCreateOrUpdateOrganizationRequest = {
      ...this.updateOrganizationRequest,
    };
    createOrganizationRequest.id = this.item.organization.id;
    this.dishesService.createOrganization(createOrganizationRequest).subscribe((res) => {
      if (!res.success) {
        this.exit();
      }
    });
    this.exit();
  }

  update(): void {
    const updateOrganizationRequest: GecoPosCreateOrUpdateOrganizationRequest = {
      ...this.updateOrganizationRequest,
    };
    updateOrganizationRequest.id = this.item.organization.id;
    this.dishesService.updateOrganization(updateOrganizationRequest).subscribe((res) => {
      if (res.success) {
        this.exit();
      }
    });

  }

  exit(): void {
    this.dialogRef.close();
  }

  private static hostOf(url: string): string | null {
    if (!url) {
      return null;
    }

    try {
      return new URL(url).host;
    } catch {
      return null;
    }
  }

  /**
   * Вебхуки geco.pos обязаны вести в хаб того же контура. Чужой адрес там не находит пайпа,
   * отвечает 400, и заказ до POS не доходит — ошибка видна только по логам сломанных заказов.
   */
  private static hubHostValidator(expectedHost: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const host = GecoPosOrganizationModalComponent.hostOf(control.value);
      if (!expectedHost || !host || host === expectedHost) {
        return null;
      }

      return { foreignHubHost: { expected: expectedHost, actual: host } };
    };
  }
}

export interface GecoPosOrganizationWrapper {
  organization: PosOrganizationModel,
  isNew: boolean,
}